import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "mt-8 mb-10 flex flex-col" }
const _hoisted_2 = { class: "text-2xl -mb-2 text-light4 uppercase font-black text-shadow truncate" }
const _hoisted_3 = { class: "text-6xl leading-tight truncate font-black" }
const _hoisted_4 = { class: "text-3xl" }
const _hoisted_5 = { class: "lowercase" }
const _hoisted_6 = /*#__PURE__*/_createElementVNode("span", null, " - ", -1)
const _hoisted_7 = { class: "lowercase" }
const _hoisted_8 = { class: "lowercase" }
const _hoisted_9 = { class: "text-light4" }
const _hoisted_10 = { class: "text-3xl" }
const _hoisted_11 = {
  key: 1,
  class: "text-light4 text-xl no-text-shadow font-semibold"
}
const _hoisted_12 = ["innerHTML"]

import { useProfileStore } from "@/store/Profile";
import { useScheduleStore } from "@/store/Schedule";
import BaseButton from "@/components/Button/BaseButton.vue";
import BUTTONTYPE from "@/components/Button/Button.types";
import { storeToRefs } from "pinia";
import { useReservation } from "@/composables/reservation";
import { computed, onMounted, watchEffect, ref } from "vue";
import { getNumberOfHours, getMinutesWithoutHours, getTimeDiff, MINUTE } from "@/helpers/Time";
import reservationStatus from "@/helpers/Reservations";
import { addMinutes } from "date-fns";
import useSound from "@/composables/sound";
import loggingService from "@/services/LoggingService";
import ErrorModal from "@/components/Modal/ErrorModal.vue";
import { useI18n } from "vue-i18n";


export default /*#__PURE__*/_defineComponent({
  setup(__props) {

const profileStore = useProfileStore();
const scheduleStore = useScheduleStore();

const { firstFutureReservation } = storeToRefs(scheduleStore);

const { startTime, endTime, pastDays, futureDays, reservationTitle, pending } = useReservation(firstFutureReservation);

const { playSound, soundPlayed } = useSound("checkin");

const isCheckinLoading = ref(false);
const showErrorModal = ref(false);
const errorMessage = ref("");

onMounted(() => {
  loggingService.info("ComingUp.vue", "🟨 Coming up view mounted");
});

const isPostComingUp = computed(() => {
  return reservationStatus.isPostComingUp(firstFutureReservation.value, scheduleStore.getTime);
});

const { t } = useI18n();

const remaining = computed(() => {
  const timeDiff = getTimeDiff(firstFutureReservation.value.startTime, scheduleStore.getTime) + MINUTE;
  const diffMinutes = getMinutesWithoutHours(timeDiff);

  if (isPostComingUp.value) {
    const left = getTimeDiff(firstFutureReservation.value.endTime, scheduleStore.getTime) + MINUTE;
    const leftHours = getNumberOfHours(left);
    const leftMinutes = getMinutesWithoutHours(left);

    const remainingHours = leftHours !== 0 ? t("remainingHours", { diffHours: leftHours }) : "";
    const remainingMinutes = leftMinutes !== 0 ? t("remainingMinutesMin", { diffMinutes: leftMinutes }) : "";

    return t("remainingLeft", { remainingHours, remainingMinutes });
  }

  return t("startsIn", { diffMinutes });
});

const untilCancel = computed(() => {
  const cancelReservationAt = addMinutes(
    firstFutureReservation.value.startTime,
    profileStore.config!.timeUntilAutoCancel
  );
  const cancelIn = getMinutesWithoutHours(getTimeDiff(cancelReservationAt, scheduleStore.getTime)) + 1;
  const cancelMinutes = cancelIn > 1 ? cancelIn : 1;
  return cancelMinutes;
});

watchEffect(() => {
  if (!firstFutureReservation.value || !profileStore.checkinAudioReminderTime) return;
  const playFrom = addMinutes(firstFutureReservation.value.startTime, profileStore.checkinAudioReminderTime);
  const playTo = addMinutes(firstFutureReservation.value.startTime, profileStore.checkinAudioReminderTime + 1);
  if (scheduleStore.getTime >= playFrom && scheduleStore.getTime <= playTo) {
    playSound();
  }
});

const onCheckIn = async () => {
  loggingService.info("ComingUp.vue", `✔️ Checkin action on reservation with ID: ${firstFutureReservation.value.id}`);
  isCheckinLoading.value = true;
  const { error } = await scheduleStore.checkIn();

  if (error) {
    showErrorModal.value = true;
    errorMessage.value = !error.response ? "tryAgain" : "somethingWentWrong";

    loggingService.error(
      "ComingUp.vue",
      `❗️ Failed to checkin reservation with ID: ${firstFutureReservation.value.id} Error: ${error}`
    );
  }
  isCheckinLoading.value = false;
  soundPlayed.value = true;
};

const showCheckinButton = computed(() => {
  return profileStore.config?.checkinRequired && !firstFutureReservation.value.checkedIn && !pending.value;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "flex flex-col text-shadow",
    onClick: _cache[1] || (_cache[1] = ($event: any) => (_ctx.$emit('closeUpcoming')))
  }, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("span", _hoisted_2, _toDisplayString(_ctx.$t("resourceName", { resourceName: _unref(profileStore).resourceName })), 1),
      _createElementVNode("h1", _hoisted_3, _toDisplayString(_unref(reservationTitle)), 1),
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("span", _hoisted_5, _toDisplayString(_unref(startTime)), 1),
        _createElementVNode("sup", null, _toDisplayString(_unref(pastDays)), 1),
        _hoisted_6,
        _createElementVNode("span", _hoisted_7, _toDisplayString(_unref(endTime)), 1),
        _createElementVNode("sup", _hoisted_8, _toDisplayString(_unref(futureDays)), 1),
        _createElementVNode("span", _hoisted_9, "・" + _toDisplayString(_unref(remaining)), 1)
      ])
    ]),
    (_unref(showCheckinButton))
      ? (_openBlock(), _createBlock(BaseButton, {
          key: 0,
          type: _unref(BUTTONTYPE).PRIMARY,
          "full-width": "",
          adjusted: "",
          disabled: isCheckinLoading.value,
          loading: isCheckinLoading.value,
          onClick: onCheckIn,
          class: "mb-3"
        }, {
          default: _withCtx(() => [
            _createElementVNode("span", _hoisted_10, _toDisplayString(_ctx.$t("checkIn")), 1)
          ]),
          _: 1
        }, 8, ["type", "disabled", "loading"]))
      : _createCommentVNode("", true),
    (_unref(isPostComingUp))
      ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
          _createElementVNode("span", {
            innerHTML: _ctx.$t('checkInWithinUntilCancelToMaintainTheReservation', { untilCancel: _unref(untilCancel) })
          }, null, 8, _hoisted_12)
        ]))
      : _createCommentVNode("", true),
    (showErrorModal.value)
      ? (_openBlock(), _createBlock(ErrorModal, {
          key: 2,
          "error-message": errorMessage.value,
          onClose: _cache[0] || (_cache[0] = ($event: any) => (showErrorModal.value = false))
        }, null, 8, ["error-message"]))
      : _createCommentVNode("", true)
  ]))
}
}

})